import React from 'react'
import { Select, Space } from 'antd'
import styled from 'styled-components'
import {
  TwitterPicker as RCTwitterPicker,
  ChromePicker as RCChromePicker,
  ChromePickerProps as RSChromePickerProps,
  ColorChangeHandler,
} from 'react-color'
import * as theme from 'src/styles/color-theme'

export type ColorPickerProps = {
  value?: string
  onChange?: (color: string) => void
  /** Custom swatch colors */
  colors?: string[]
}

const defaultColors = [
  theme.gray1000,
  theme.gray100,
  '#FF6900',
  '#FCB900',
  '#00D084',
  '#0693E3',
  '#ABB8C3',
  '#EB144C',
  '#F78DA7',
  '#9900EF',
]

const TwitterColorPicker = ({ value, onChange, colors }: ColorPickerProps) => {
  return (
    <RCTwitterPicker
      colors={colors ?? defaultColors}
      color={value}
      onChange={(c) => onChange?.(c.hex)}
      width="192px"
      styles={{
        default: {
          body: {
            width: 192,
            paddingTop: 8,
            paddingLeft: 8,
            paddingRight: 2,
            paddingBottom: 2,
          },
          swatch: {
            width: 20,
            height: 20,
            boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 4px',
          },
          input: {
            height: 20,
            width: 58,
          },
          label: {
            height: 20,
          },
          hash: {
            height: 22,
          },
        },
      }}
      triangle="hide"
    />
  )
}

const SketchColorPickerPrefix = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid ${theme.gray500};
  background-color: ${(props) => props.color};

  position: absolute;
  z-index: 10;
  left: 10px;
  top: 8px;
`

const StyledColorSelect = styled(Select)`
  min-width: 100px;
  &.ant-select-single .ant-select-selector {
    padding-left: 36px;
  }
`

const chromePickerStyles: RSChromePickerProps['styles'] = {
  default: {
    picker: {
      padding: '8px 10px 0',
      boxShadow: 'none',
    },
    saturation: {
      borderRadius: 2,
    },
  },
}
type TChromeColorPickerProps = {
  value?: string
  onChange?: (color: string) => void
  disabled?: boolean
}

const ChromeColorPicker = ({
  onChange,
  disabled = false,
  value = '#000000',
}: TChromeColorPickerProps) => {
  const handleChange: ColorChangeHandler = (color) => {
    onChange?.(color.hex.toUpperCase())
  }

  return (
    <Space.Compact
      style={{ cursor: 'pointer', position: 'relative', width: '100%' }}
    >
      <SketchColorPickerPrefix color={value} />
      <StyledColorSelect
        value={value}
        disabled={disabled}
        dropdownMatchSelectWidth={false}
        dropdownStyle={{ minWidth: 200 }}
        dropdownRender={() => (
          <RCChromePicker
            disableAlpha
            color={value}
            onChangeComplete={handleChange}
            styles={chromePickerStyles}
          />
        )}
      />
    </Space.Compact>
  )
}

export { TwitterColorPicker, ChromeColorPicker }
